/**
 * MuiShipping - Optimized Shipping Management Page
 *
 * OPTIMIZATION NOTES (Phase 1.2):
 * - Uses server-side search with 300ms debounce
 * - Uses fetchPaginated with status=INVOICED filter
 * - Analytics fetched separately with lightweight count queries
 * - Customer data still fetched for label lookups (required for UI)
 * - Maintains all existing functionality: selection, route map, printing
 *
 * @see OPTIMIZATION_PLAN.md for full details
 */

import React, { useEffect, useState, useMemo, useCallback, memo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import SearchIcon from "@mui/icons-material/Search"
import HomeIcon from "@mui/icons-material/Home"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import RefreshIcon from "@mui/icons-material/Refresh"
import AssessmentIcon from "@mui/icons-material/Assessment"
import PrintIcon from "@mui/icons-material/Print"
import SendIcon from "@mui/icons-material/Send"
import PersonIcon from "@mui/icons-material/Person"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ClearIcon from "@mui/icons-material/Clear"

import {
  getCustomers,
  getCustomerDetail,
  patchOrder,
  postShipping,
  getVehicles,
  getAppRoles,
  getAppUsersByRole,
  fetchPaginated,
} from "helpers/api_helper"
import { GET_ORDERS } from "helpers/api_urls"
import { printInvoice } from "./PrintInvoice"
import { printDriver } from "./printshippingDriver"
import OptimalRouteMap from "./optimalroute"
import { getCompanyName } from "../../context/AppConfigContext"

/**
 * Fetch invoiced orders with optional search filter
 * Server-side filtering for search across customer_name and notes
 */
async function fetchInvoicedOrders(options = {}) {
  const { search = null, limit = 1000 } = options

  // Build filter: always filter by INVOICED status
  let filter = "status=INVOICED"

  return fetchPaginated(GET_ORDERS, {
    limit,
    offset: 0,
    filter,
    search,
    searchFields: ["customer_name", "name", "notes", "label"],
    orderBy: "expected_delivery_date",
    orderDir: "ASC",
  })
}

/**
 * Fetch analytics for INVOICED orders (lightweight - counts only where needed)
 */
async function fetchShippingAnalytics() {
  try {
    // Get total count of INVOICED orders
    const result = await fetchPaginated(GET_ORDERS, {
      limit: 0,
      filter: "status=INVOICED",
    })
    return { totalCount: result.total }
  } catch (error) {
    console.error("Error fetching shipping analytics:", error)
    return { totalCount: 0 }
  }
}

const MuiShipping = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  document.title = t("Shipping") + " | FlexFlow ERP"

  // Data state
  const [orders, setOrders] = useState([])
  const [customers, setCustomers] = useState([])
  const [drivers, setDrivers] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)

  // Search state with debounce
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const searchTimeoutRef = useRef(null)

  // Selection state
  const [selectedOrders, setSelectedOrders] = useState([])
  const [shippingDetails, setShippingDetails] = useState({ driver: "", driver_id: null, type: "", car: "" })
  const [extraInfo, setExtraInfo] = useState({})

  // UI state
  const [analyticsExpanded, setAnalyticsExpanded] = useState(true)
  const [routeDetails, setRouteDetails] = useState([])
  const [sending, setSending] = useState(false)

  // ============================================================================
  // DEBOUNCED SEARCH
  // ============================================================================
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set new timeout for debounce (300ms)
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [search])

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Fetch orders when debounced search changes
  const fetchOrders = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchInvoicedOrders({
        search: debouncedSearch || null,
      })
      setOrders(result.data || [])
    } catch (err) {
      console.error("Error fetching orders:", err)
      setOrders([])
    }
    setLoading(false)
  }, [debouncedSearch])

  // Fetch supporting data (customers, drivers, vehicles)
  const fetchSupportingData = useCallback(async () => {
    try {
      const [customersRes, vehiclesRes] = await Promise.all([
        getCustomers(),
        getVehicles().catch(() => []),
      ])
      setCustomers(customersRes || [])
      setVehicles(vehiclesRes || [])

      // Fetch drivers from FlexFlow (users with "driver" role)
      try {
        const roles = await getAppRoles()
        const driverRole = roles?.find(r => r.name?.toLowerCase() === "driver")
        if (driverRole) {
          const driversRes = await getAppUsersByRole(driverRole.id)
          setDrivers(driversRes || [])
        } else {
          setDrivers([])
        }
      } catch (err) {
        console.error("Error fetching drivers from FlexFlow:", err)
        setDrivers([])
      }
    } catch (err) {
      console.error("Error fetching supporting data:", err)
    }
  }, [])

  // Initial data load
  useEffect(() => {
    fetchSupportingData()
  }, [fetchSupportingData])

  // Fetch orders when search changes
  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Refresh all data
  const refreshData = useCallback(() => {
    fetchOrders()
    fetchSupportingData()
  }, [fetchOrders, fetchSupportingData])

  // ============================================================================
  // ANALYTICS (computed from loaded orders)
  // ============================================================================
  const analytics = useMemo(() => {
    let totalItems = 0
    let totalValue = 0
    const customerSet = new Set()
    const driverCounts = {}

    orders.forEach((order) => {
      try {
        const totals = JSON.parse(order.totals || "{}")
        totalItems += Number(totals.totalItems || 0)
        totalValue += Number(totals.totalOrder || 0)
      } catch {}
      if (order.customer_id) customerSet.add(order.customer_id)
      if (order.shipping_by) {
        driverCounts[order.shipping_by] = (driverCounts[order.shipping_by] || 0) + 1
      }
    })

    const byDriver = Object.entries(driverCounts).map(([driver, count]) => ({ driver, count }))

    return {
      totalOrders: orders.length,
      totalItems,
      totalValue,
      uniqueCustomers: customerSet.size,
      byDriver,
    }
  }, [orders])

  // ============================================================================
  // SELECTION & ADDRESSES
  // ============================================================================
  const selectedAddresses = useMemo(() => {
    return selectedOrders.map((orderId) => {
      const order = orders.find((o) => o.id === orderId)
      const customer = customers.find((c) => c.id === order?.customer_id)
      return customer?.address || order?.delivery_address || ""
    }).filter(Boolean)
  }, [selectedOrders, orders, customers])

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedOrders(newSelection)
  }, [])

  const handleSearchChange = useCallback((value) => {
    setSearch(value)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearch("")
  }, [])

  const handleSendOrders = async () => {
    if (selectedOrders.length === 0 || !shippingDetails.driver) return
    setSending(true)

    try {
      const payload = selectedOrders.map((orderId) => ({
        id: orderId,
        status: "ON SHIPPING",
        shipping: JSON.stringify({ ...shippingDetails, extra: extraInfo[orderId] || "" }),
        shipping_by: shippingDetails.driver,
      }))

      await Promise.all(payload.map((orderData) => patchOrder({ resource: [orderData] })))

      const shippingPayload = {
        driver: shippingDetails.driver,
        driver_id: shippingDetails.driver_id,
        status: "PENDING",
        orders: JSON.stringify(selectedOrders.map((id) => ({ id }))),
        route: JSON.stringify(selectedAddresses),
      }
      await postShipping({ resource: [shippingPayload] })

      setSelectedOrders([])
      setShippingDetails({ driver: "", driver_id: null, type: "", car: "" })
      setExtraInfo({})
      fetchOrders()
    } catch (err) {
      console.error("Error sending orders:", err)
    }
    setSending(false)
  }

  const handlePrintDriverPlan = async () => {
    const companyName = getCompanyName()
    const today = moment().format("DD/MM/YYYY")
    const printTime = moment().format("HH:mm")

    const selectedOverviewPromises = selectedOrders.map(async (orderId) => {
      const order = orders.find((o) => o.id === orderId)
      let totalOrder = 0
      let totalItems = 0
      try {
        const totals = JSON.parse(order?.totals || "{}")
        totalOrder = totals.totalOrder || 0
        totalItems = totals.totalItems || 0
      } catch {}
      const customer = await getCustomerDetail(order?.customer_id)
      if (!customer?.length) return null

      // Parse address - handle JSON format
      let address = order.delivery_address || customer[0].address || ""
      if (typeof address === "string" && address.startsWith("{")) {
        try {
          const parsed = JSON.parse(address)
          address = parsed.address || parsed.street || address
        } catch {}
      }

      return {
        orderId: order.id,
        name: order.name,
        label: customer[0].label || "",
        address,
        city: customer[0].city || "",
        postcode: customer[0].postcode || "",
        phone: customer[0].phone || "",
        totalItems,
        totalOrder: Number(totalOrder).toFixed(2),
        extraInfo: extraInfo[orderId] || "",
      }
    })

    const selectedOverview = (await Promise.all(selectedOverviewPromises)).filter(Boolean)

    // Calculate totals
    const grandTotalItems = selectedOverview.reduce((sum, o) => sum + (Number(o.totalItems) || 0), 0)
    const grandTotalValue = selectedOverview.reduce((sum, o) => sum + (Number(o.totalOrder) || 0), 0)

    // Calculate total distance and duration from routeDetails
    let totalDistance = 0
    let totalDuration = 0
    routeDetails.forEach((leg) => {
      const distMatch = leg.distance?.match(/[\d.]+/)
      const durMatch = leg.duration?.match(/[\d.]+/)
      if (distMatch) totalDistance += parseFloat(distMatch[0])
      if (durMatch) totalDuration += parseFloat(durMatch[0])
    })

    // Generate delivery cards (ordered A, B, C...)
    const deliveryCards = selectedOverview
      .map((o, index) => {
        const stopLetter = String.fromCharCode(65 + index)
        const routeLeg = routeDetails[index] || {}
        return `
        <div class="delivery-card">
          <div class="stop-badge">${stopLetter}</div>
          <div class="delivery-info">
            <div class="customer-name">${o.name}</div>
            ${o.label ? `<div class="customer-label">${o.label}</div>` : ""}
            <div class="address">${o.address}${o.city ? `, ${o.city}` : ""}${o.postcode ? ` ${o.postcode}` : ""}</div>
            ${o.phone ? `<div class="phone">📞 ${o.phone}</div>` : ""}
            <div class="order-details">
              <span class="items">${o.totalItems} ${t("items")}</span>
              <span class="total">€${o.totalOrder}</span>
              <span class="order-id">#${o.orderId}</span>
            </div>
            ${o.extraInfo ? `<div class="extra-info">📝 ${o.extraInfo}</div>` : ""}
          </div>
          <div class="route-info">
            ${routeLeg.distance ? `<div class="distance">${routeLeg.distance}</div>` : ""}
            ${routeLeg.duration ? `<div class="duration">${routeLeg.duration}</div>` : ""}
          </div>
          <div class="checkbox-area">
            <div class="checkbox"></div>
            <span>Delivered</span>
          </div>
        </div>`
      })
      .join("")

    // Generate static map URL using Google Static Maps API
    const startAddress = "Psichari 1, Metamorfosi 144 52, Greece"
    const mapMarkers = selectedOverview
      .map((o, index) => {
        const label = String.fromCharCode(65 + index)
        return `markers=color:blue%7Clabel:${label}%7C${encodeURIComponent(o.address)}`
      })
      .join("&")
    const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?size=800x400&maptype=roadmap&markers=color:green%7Clabel:S%7C${encodeURIComponent(startAddress)}&${mapMarkers}&key=AIzaSyAvDka_HEWufYVqW_m2KzIfSJEjd9O-gYI`

    const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>Delivery Route - ${shippingDetails.driver}</title>
        <style>
          @page { size: A4 portrait; margin: 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            font-size: 12px;
            color: #333;
            line-height: 1.4;
          }

          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding-bottom: 15px;
            border-bottom: 2px solid #3b82f6;
            margin-bottom: 15px;
          }

          .company-info {
            text-align: left;
          }

          .company-name {
            font-size: 22px;
            font-weight: 700;
            color: #1e40af;
          }

          .document-title {
            font-size: 14px;
            color: #6b7280;
            margin-top: 4px;
          }

          .driver-info {
            text-align: right;
          }

          .driver-name {
            font-size: 18px;
            font-weight: 600;
            color: #111827;
          }

          .driver-details {
            font-size: 11px;
            color: #6b7280;
            margin-top: 4px;
          }

          .summary-bar {
            display: flex;
            justify-content: space-between;
            background: #f8fafc;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 15px;
          }

          .summary-item {
            text-align: center;
          }

          .summary-value {
            font-size: 18px;
            font-weight: 700;
            color: #111827;
          }

          .summary-label {
            font-size: 10px;
            color: #6b7280;
            text-transform: uppercase;
          }

          .section-title {
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 10px;
            padding-bottom: 5px;
            border-bottom: 1px solid #e5e7eb;
          }

          .delivery-card {
            display: flex;
            align-items: stretch;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-bottom: 10px;
            overflow: hidden;
            page-break-inside: avoid;
          }

          .stop-badge {
            width: 40px;
            min-width: 40px;
            background: #3b82f6;
            color: white;
            font-size: 20px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .delivery-info {
            flex: 1;
            padding: 10px 12px;
          }

          .customer-name {
            font-size: 14px;
            font-weight: 600;
            color: #111827;
          }

          .customer-label {
            font-size: 11px;
            color: #6b7280;
            font-style: italic;
          }

          .address {
            font-size: 12px;
            color: #374151;
            margin-top: 4px;
          }

          .phone {
            font-size: 12px;
            color: #3b82f6;
            margin-top: 4px;
          }

          .order-details {
            display: flex;
            gap: 12px;
            margin-top: 6px;
            font-size: 11px;
          }

          .items {
            background: #dbeafe;
            color: #1d4ed8;
            padding: 2px 8px;
            border-radius: 4px;
          }

          .total {
            background: #dcfce7;
            color: #166534;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: 600;
          }

          .order-id {
            color: #9ca3af;
          }

          .extra-info {
            font-size: 11px;
            color: #f59e0b;
            margin-top: 4px;
            font-style: italic;
          }

          .route-info {
            width: 70px;
            min-width: 70px;
            background: #f8fafc;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 8px;
            border-left: 1px solid #e5e7eb;
          }

          .distance {
            font-size: 12px;
            font-weight: 600;
            color: #374151;
          }

          .duration {
            font-size: 10px;
            color: #6b7280;
          }

          .checkbox-area {
            width: 70px;
            min-width: 70px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 8px;
            border-left: 1px solid #e5e7eb;
          }

          .checkbox {
            width: 24px;
            height: 24px;
            border: 2px solid #d1d5db;
            border-radius: 4px;
            margin-bottom: 4px;
          }

          .checkbox-area span {
            font-size: 9px;
            color: #9ca3af;
          }

          .map-section {
            margin-top: 20px;
            page-break-inside: avoid;
          }

          .map-image {
            width: 100%;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }

          .footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid #e5e7eb;
            font-size: 10px;
            color: #9ca3af;
            display: flex;
            justify-content: space-between;
          }

          .signature-area {
            margin-top: 30px;
            display: flex;
            gap: 40px;
          }

          .signature-box {
            flex: 1;
            text-align: center;
          }

          .signature-line {
            border-top: 1px solid #d1d5db;
            margin-top: 40px;
            padding-top: 5px;
            font-size: 10px;
            color: #6b7280;
          }

          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <div class="company-name">${companyName}</div>
            <div class="document-title">${t("Delivery Route Sheet")}</div>
          </div>
          <div class="driver-info">
            <div class="driver-name">${shippingDetails.driver || t("Driver")}</div>
            <div class="driver-details">
              ${shippingDetails.car ? `${t("Vehicle")}: ${shippingDetails.car}` : ""}
              ${shippingDetails.type ? ` • ${shippingDetails.type}` : ""}
            </div>
            <div class="driver-details">${today} ${t("at")} ${printTime}</div>
          </div>
        </div>

        <div class="summary-bar">
          <div class="summary-item">
            <div class="summary-value">${selectedOverview.length}</div>
            <div class="summary-label">${t("Deliveries")}</div>
          </div>
          <div class="summary-item">
            <div class="summary-value">${grandTotalItems}</div>
            <div class="summary-label">${t("Total Items")}</div>
          </div>
          <div class="summary-item">
            <div class="summary-value">€${grandTotalValue.toFixed(2)}</div>
            <div class="summary-label">${t("Total Value")}</div>
          </div>
          <div class="summary-item">
            <div class="summary-value">${totalDistance.toFixed(1)} km</div>
            <div class="summary-label">${t("Distance")}</div>
          </div>
          <div class="summary-item">
            <div class="summary-value">${Math.round(totalDuration)} min</div>
            <div class="summary-label">${t("Est. Duration")}</div>
          </div>
        </div>

        <div class="section-title">${t("Delivery Stops")} (${t("Optimized Route Order")})</div>
        ${deliveryCards}

        <div class="map-section">
          <div class="section-title">${t("Route Map")}</div>
          <img src="${staticMapUrl}" alt="Route Map" class="map-image" />
        </div>

        <div class="signature-area">
          <div class="signature-box">
            <div class="signature-line">${t("Driver Signature")}</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">${t("Notes")}</div>
          </div>
        </div>

        <div class="footer">
          <span>${companyName} - ${t("Delivery Route Sheet")}</span>
          <span>${t("Printed")}: ${today} ${printTime}</span>
        </div>
      </body>
    </html>`

    printDriver(htmlContent)
  }

  // ============================================================================
  // DATAGRID CONFIGURATION
  // ============================================================================
  const columns = useMemo(
    () => [
      { field: "id", headerName: t("Order ID"), width: 90 },
      {
        field: "expected_delivery_date",
        headerName: t("Delivery"),
        width: 100,
        valueFormatter: (value) => (value ? moment(value).format("DD/MM/YY") : ""),
      },
      { field: "name", headerName: t("Customer"), flex: 1, minWidth: 150 },
      {
        field: "label",
        headerName: t("Label"),
        width: 120,
        valueGetter: (value, row) => {
          const customer = customers.find((c) => c.id === row.customer_id)
          return customer?.label || ""
        },
      },
      {
        field: "totalItems",
        headerName: t("Items"),
        width: 80,
        valueGetter: (value, row) => {
          try {
            return JSON.parse(row.totals || "{}").totalItems || 0
          } catch {
            return 0
          }
        },
      },
      {
        field: "totalOrder",
        headerName: t("Total"),
        width: 100,
        valueGetter: (value, row) => {
          try {
            return Number(JSON.parse(row.totals || "{}").totalOrder || 0).toFixed(2)
          } catch {
            return "0.00"
          }
        },
      },
      {
        field: "print",
        headerName: "",
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              printInvoice(params.row)
            }}
          >
            <PrintIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
          </IconButton>
        ),
      },
    ],
    [t, customers]
  )

  const dataGridSx = useMemo(
    () => ({
      border: "none",
      "& .MuiDataGrid-columnHeaders": { bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
        fontSize: "0.75rem",
        color: "#6b7280",
        textTransform: "uppercase",
      },
      "& .MuiDataGrid-row": { cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } },
      "& .MuiDataGrid-row.Mui-selected": { bgcolor: "#dcfce7", "&:hover": { bgcolor: "#bbf7d0" } },
      "& .MuiDataGrid-cell": { borderBottom: "1px solid #f3f4f6", fontSize: "0.875rem" },
    }),
    []
  )

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Box sx={{ px: 1, py: 1, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header */}
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 0.75,
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          bgcolor: "#fff",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Breadcrumbs separator="›" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
            <Link
              component="button"
              onClick={() => navigate("/")}
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.75rem", "&:hover": { color: "#3b82f6" } }}
            >
              <HomeIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {t("Home")}
            </Link>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center" }}>
              <LocalShippingIcon sx={{ fontSize: 14, mr: 0.5, color: "#3b82f6" }} />
              {t("Shipping")}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            size="small"
            label={`${orders.length} ${t("ready")}`}
            sx={{ height: 22, fontSize: "0.7rem", bgcolor: "#dbeafe", color: "#1d4ed8" }}
          />
          <IconButton size="small" onClick={refreshData} disabled={loading} sx={{ p: 0.5 }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Paper>

      {/* Compact Analytics */}
      <Paper elevation={0} sx={{ mb: 1, borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            bgcolor: analyticsExpanded ? "#f8fafc" : "#fff",
            borderBottom: analyticsExpanded ? "1px solid #e5e7eb" : "none",
          }}
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ bgcolor: "#3b82f6", width: 28, height: 28 }}>
                <AssessmentIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827" }}>
                {t("Analytics")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Chip size="small" label={`${analytics.totalOrders} ${t("orders")}`} sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#dbeafe", color: "#1d4ed8" }} />
              <Chip size="small" label={`${analytics.totalItems} ${t("items")}`} sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#dcfce7", color: "#166534" }} />
              <Chip size="small" label={`€${analytics.totalValue.toFixed(0)}`} sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#fef3c7", color: "#92400e" }} />
            </Stack>
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {analyticsExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 1.5 }}>
            <Grid container spacing={1}>
              {[
                { value: analytics.totalOrders, label: t("Orders"), color: "#3b82f6" },
                { value: analytics.totalItems, label: t("Items"), color: "#10b981" },
                { value: analytics.uniqueCustomers, label: t("Customers"), color: "#f59e0b" },
                { value: `€${analytics.totalValue.toFixed(0)}`, label: t("Value"), color: "#8b5cf6" },
              ].map((stat) => (
                <Grid item xs={3} key={stat.label}>
                  <Box sx={{ textAlign: "center", py: 1, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e5e7eb" }}>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
                    <Typography sx={{ fontSize: "0.65rem", color: "#6b7280", textTransform: "uppercase" }}>{stat.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Main Content - Two Column Layout */}
      <Grid container spacing={1}>
        {/* Orders Table - Left Column */}
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden", height: "100%" }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e5e7eb", bgcolor: "#fff" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  placeholder={t("Search...")}
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  size="small"
                  sx={{ flex: 1, maxWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 1, height: 32 }, "& input": { py: 0.5, fontSize: "0.813rem" } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} /></InputAdornment>,
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.25 }}>
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Chip size="small" label={`${orders.length} ${t("orders")}`} sx={{ height: 22, fontSize: "0.7rem", bgcolor: "#f3f4f6", color: "#374151" }} />
                {selectedOrders.length > 0 && (
                  <Chip size="small" label={`${selectedOrders.length} ${t("selected")}`} sx={{ height: 22, fontSize: "0.7rem", bgcolor: "#dcfce7", color: "#166534", fontWeight: 600 }} />
                )}
                {loading && <CircularProgress size={18} />}
              </Stack>
            </Box>

            {loading && orders.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <DataGrid
                rows={orders}
                columns={columns}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectedOrders}
                onRowSelectionModelChange={handleSelectionChange}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={{
                  ...dataGridSx,
                  height: 480,
                  "& .MuiDataGrid-cell": { ...dataGridSx["& .MuiDataGrid-cell"], py: 0.5, fontSize: "0.813rem" },
                  "& .MuiDataGrid-columnHeaderTitle": { ...dataGridSx["& .MuiDataGrid-columnHeaderTitle"], fontSize: "0.7rem" },
                }}
              />
            )}
          </Paper>
        </Grid>

        {/* Shipping Panel - Right Column */}
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e5e7eb", bgcolor: "#f8fafc" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827" }}>
                  {t("Shipping Details")}
                </Typography>
                {selectedOrders.length > 0 && (
                  <Chip size="small" label={`${selectedOrders.length} ${t("orders")}`} sx={{ height: 20, fontSize: "0.65rem", bgcolor: "#3b82f6", color: "#fff" }} />
                )}
              </Stack>
            </Box>

            <Box sx={{ p: 1.5 }}>
              {selectedOrders.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, color: "#9ca3af" }}>
                  <LocalShippingIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                  <Typography sx={{ fontSize: "0.813rem" }}>{t("Select orders to ship")}</Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {/* Shipping Form */}
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontSize: "0.813rem" }}>{t("Shipping Type")}</InputLabel>
                        <Select
                          value={shippingDetails.type}
                          label={t("Shipping Type")}
                          onChange={(e) => setShippingDetails((d) => ({ ...d, type: e.target.value, driver: "", driver_id: null, car: "" }))}
                          sx={{ "& .MuiSelect-select": { py: 1, fontSize: "0.813rem" } }}
                        >
                          <MenuItem value="Driver">{t("Driver")}</MenuItem>
                          <MenuItem value="Courier">{t("Courier")}</MenuItem>
                          <MenuItem value="ACS">ACS</MenuItem>
                          <MenuItem value="DHL">DHL</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {shippingDetails.type === "Driver" ? (
                      <>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontSize: "0.813rem" }}>{t("Driver")}</InputLabel>
                            <Select
                              value={shippingDetails.driver_id || ""}
                              label={t("Driver")}
                              onChange={(e) => {
                                const selectedDriver = drivers.find(d => d.id === e.target.value)
                                if (selectedDriver) {
                                  const fullName = `${selectedDriver.first_name || ""}${selectedDriver.last_name ? ` ${selectedDriver.last_name}` : ""}`.trim() || selectedDriver.email
                                  setShippingDetails((d) => ({ ...d, driver: fullName, driver_id: selectedDriver.id }))
                                }
                              }}
                              sx={{ "& .MuiSelect-select": { py: 1, fontSize: "0.813rem" } }}
                            >
                              {drivers.map((driver) => (
                                <MenuItem key={driver.id} value={driver.id}>
                                  {`${driver.first_name || ""}${driver.last_name ? ` ${driver.last_name}` : ""}`.trim() || driver.email}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontSize: "0.813rem" }}>{t("Vehicle")}</InputLabel>
                            <Select
                              value={shippingDetails.car}
                              label={t("Vehicle")}
                              onChange={(e) => setShippingDetails((d) => ({ ...d, car: e.target.value }))}
                              sx={{ "& .MuiSelect-select": { py: 1, fontSize: "0.813rem" } }}
                            >
                              {vehicles.map((vehicle) => (
                                <MenuItem key={vehicle.id} value={vehicle.license_plate}>
                                  {vehicle.license_plate}{vehicle.make ? ` - ${vehicle.make}` : ""}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label={t("Contact")}
                            value={shippingDetails.driver}
                            onChange={(e) => setShippingDetails((d) => ({ ...d, driver: e.target.value }))}
                            sx={{ "& input": { py: 1, fontSize: "0.813rem" } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label={t("Tracking")}
                            value={shippingDetails.car}
                            onChange={(e) => setShippingDetails((d) => ({ ...d, car: e.target.value }))}
                            sx={{ "& input": { py: 1, fontSize: "0.813rem" } }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                      onClick={handlePrintDriverPlan}
                      disabled={!shippingDetails.driver}
                      sx={{ flex: 1, textTransform: "none", fontSize: "0.75rem", py: 0.75, borderColor: "#e5e7eb", color: "#374151" }}
                    >
                      {t("Print Route")}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                      onClick={handleSendOrders}
                      disabled={sending || !shippingDetails.driver}
                      sx={{ flex: 1, textTransform: "none", fontSize: "0.75rem", py: 0.75, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}
                    >
                      {t("Ship Orders")}
                    </Button>
                  </Stack>

                  {/* Route Map - Larger */}
                  {selectedAddresses.length > 0 && (
                    <Box sx={{ mt: 1, border: "1px solid #e5e7eb", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ px: 1.5, py: 0.75, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151" }}>
                          {t("Route Map")} • {selectedAddresses.length} {t("stops")}
                        </Typography>
                      </Box>
                      <Box sx={{ "& > div": { "& > div:first-of-type": { mb: 1 } } }}>
                        <OptimalRouteMap addresses={selectedAddresses} onRouteCalculated={setRouteDetails} />
                      </Box>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default MuiShipping
